import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Heart, Handshake, Users, Shield, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getInvolvedOptions, donationAmounts } from '../data/mock';

const GetInvolved = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('donate');
  const [donationAmount, setDonationAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const iconMap = {
    Heart: Heart,
    Handshake: Handshake,
    Users: Users,
    Shield: Shield
  };

  const handleDonationSubmit = (e) => {
    e.preventDefault();
    const amount = donationAmount === 'custom' ? customAmount : donationAmount;
    // Mock donation processing
    toast.success(`Thank you for your £${amount} ${isRecurring ? 'monthly ' : ''}donation! Redirecting to payment...`);
  };

  const handleVolunteerSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    // Mock form submission
    toast.success('Thank you for your interest! We\'ll be in touch within 48 hours.');
    e.target.reset();
  };

  const handlePartnerSubmit = (e) => {
    e.preventDefault();
    toast.success('Partnership inquiry submitted! Our team will contact you shortly.');
    e.target.reset();
  };

  const handleTrusteeSubmit = (e) => {
    e.preventDefault();
    toast.success('Application received! We\'ll review and respond within one week.');
    e.target.reset();
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-[#D4AF37] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Get Involved</h1>
          <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
            Your time, gifts, expertise, and partnership can transform lives. Choose how you'd like to make an impact.
          </p>
        </div>
      </section>

      {/* Options Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {getInvolvedOptions.map((option) => {
              const Icon = iconMap[option.icon];
              return (
                <Card
                  key={option.id}
                  className={`cursor-pointer border-2 transition-all duration-300 hover:shadow-lg ${
                    activeTab === option.id ? 'border-[#2D5F3F] shadow-lg' : 'border-gray-100'
                  }`}
                  onClick={() => setActiveTab(option.id)}
                >
                  <CardContent className="p-6 text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 ${
                      option.color === 'green' ? 'bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A]' : 'bg-[#D4AF37]'
                    }`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#2D5F3F] mb-2">{option.title}</h3>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Forms Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="hidden">
              <TabsTrigger value="donate">Donate</TabsTrigger>
              <TabsTrigger value="partner">Partner</TabsTrigger>
              <TabsTrigger value="volunteer">Volunteer</TabsTrigger>
              <TabsTrigger value="trustee">Trustee</TabsTrigger>
            </TabsList>

            {/* Donate Tab */}
            <TabsContent value="donate">
              <Card className="border-none shadow-xl">
                <CardContent className="p-8 md:p-12">
                  <div className="flex items-center mb-6">
                    <div className="bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] p-3 rounded-xl mr-4">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-[#2D5F3F]">Make a Donation</h2>
                      <p className="text-gray-600">Your gift delivers £12 of impact for every £1</p>
                    </div>
                  </div>

                  <form onSubmit={handleDonationSubmit} className="space-y-6">
                    <div>
                      <Label className="text-lg font-semibold text-gray-700 mb-4 block">Select Amount</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {donationAmounts.map((amount) => (
                          <button
                            key={amount.value}
                            type="button"
                            onClick={() => setDonationAmount(amount.value)}
                            className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                              donationAmount === amount.value
                                ? 'border-[#2D5F3F] bg-[#2D5F3F]/5 shadow-md'
                                : 'border-gray-200 hover:border-[#2D5F3F]'
                            }`}
                          >
                            <div className="text-2xl font-bold text-[#2D5F3F] mb-1">{amount.label}</div>
                            <div className="text-xs text-gray-600">{amount.impact}</div>
                          </button>
                        ))}
                      </div>
                      
                      {donationAmount === 'custom' && (
                        <div className="mt-4">
                          <Input
                            type="number"
                            placeholder="Enter custom amount"
                            value={customAmount}
                            onChange={(e) => setCustomAmount(e.target.value)}
                            className="text-lg"
                            required
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-3 bg-gray-50 p-4 rounded-xl">
                      <input
                        type="checkbox"
                        id="recurring"
                        checked={isRecurring}
                        onChange={(e) => setIsRecurring(e.target.checked)}
                        className="w-5 h-5 text-[#2D5F3F] rounded focus:ring-[#2D5F3F]"
                      />
                      <Label htmlFor="recurring" className="text-base font-medium cursor-pointer">
                        Make this a monthly donation (cancel anytime)
                      </Label>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="donor-name">Full Name *</Label>
                        <Input id="donor-name" required className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="donor-email">Email Address *</Label>
                        <Input id="donor-email" type="email" required className="mt-2" />
                      </div>
                    </div>

                    <div className="bg-[#D4AF37]/10 border-2 border-[#D4AF37] rounded-xl p-6">
                      <div className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-gray-700">
                          <strong>Gift Aid:</strong> UK taxpayers can increase their donation by 25% at no cost. We'll add Gift Aid details on the next page.
                        </p>
                      </div>
                    </div>

                    <Button type="submit" size="lg" className="w-full bg-gradient-to-r from-[#2D5F3F] to-[#3A7D4A] hover:from-[#234A32] hover:to-[#2D5F3F] text-white font-semibold py-6 text-lg">
                      <Heart className="w-5 h-5 mr-2" />
                      Proceed to Secure Payment
                    </Button>

                    <p className="text-xs text-center text-gray-500">
                      Payments processed securely via Stripe. We never store your card details.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Partner Tab */}
            <TabsContent value="partner">
              <Card className="border-none shadow-xl">
                <CardContent className="p-8 md:p-12">
                  <div className="flex items-center mb-6">
                    <div className="bg-[#D4AF37] p-3 rounded-xl mr-4">
                      <Handshake className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-[#2D5F3F]">Become a Partner</h2>
                      <p className="text-gray-600">Churches, businesses, and organizations welcome</p>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-[#2D5F3F] mb-4">Partnership Opportunities</h3>
                    <ul className="space-y-3">
                      {[
                        "Host a collection hub at your location",
                        "Sponsor a container (£3,500 delivers 300+ items)",
                        "Corporate volunteer days",
                        "In-kind donations (transport, warehousing, expertise)",
                        "Prayer partnerships and awareness campaigns"
                      ].map((opportunity, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Check className="w-5 h-5 text-[#2D5F3F] mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{opportunity}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <form onSubmit={handlePartnerSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="partner-org">Organization Name *</Label>
                        <Input id="partner-org" required className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="partner-type">Organization Type *</Label>
                        <select id="partner-type" required className="w-full mt-2 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D5F3F]">
                          <option value="">Select type</option>
                          <option value="church">Church</option>
                          <option value="business">Business</option>
                          <option value="healthcare">Healthcare Provider</option>
                          <option value="charity">Other Charity/NGO</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="partner-contact-name">Contact Name *</Label>
                        <Input id="partner-contact-name" required className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="partner-email">Email Address *</Label>
                        <Input id="partner-email" type="email" required className="mt-2" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="partner-phone">Phone Number</Label>
                      <Input id="partner-phone" type="tel" className="mt-2" />
                    </div>

                    <div>
                      <Label htmlFor="partner-interest">Tell us about your interest in partnering *</Label>
                      <Textarea id="partner-interest" rows={5} required className="mt-2" />
                    </div>

                    <Button type="submit" size="lg" className="w-full bg-[#D4AF37] hover:bg-[#C19B2C] text-white font-semibold py-6 text-lg">
                      <Handshake className="w-5 h-5 mr-2" />
                      Submit Partnership Inquiry
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Volunteer Tab */}
            <TabsContent value="volunteer">
              <Card className="border-none shadow-xl">
                <CardContent className="p-8 md:p-12">
                  <div className="flex items-center mb-6">
                    <div className="bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] p-3 rounded-xl mr-4">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-[#2D5F3F]">Volunteer With Us</h2>
                      <p className="text-gray-600">Flexible opportunities across the UK</p>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-[#2D5F3F] mb-4">Volunteer Roles</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {[
                        "Equipment collection & sorting",
                        "Wheelchair refurbishment & testing",
                        "Admin & database management",
                        "Fundraising events support",
                        "Social media & communications",
                        "Driver (equipment collection)"
                      ].map((role, index) => (
                        <div key={index} className="flex items-start space-x-3 bg-gray-50 p-3 rounded-lg">
                          <Check className="w-5 h-5 text-[#2D5F3F] mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{role}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <form onSubmit={handleVolunteerSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="volunteer-name">Full Name *</Label>
                        <Input id="volunteer-name" name="name" required className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="volunteer-email">Email Address *</Label>
                        <Input id="volunteer-email" name="email" type="email" required className="mt-2" />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="volunteer-phone">Phone Number</Label>
                        <Input id="volunteer-phone" name="phone" type="tel" className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="volunteer-location">Your Location (City) *</Label>
                        <Input id="volunteer-location" name="location" required className="mt-2" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="volunteer-availability">Availability *</Label>
                      <select id="volunteer-availability" name="availability" required className="w-full mt-2 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D5F3F]">
                        <option value="">Select availability</option>
                        <option value="weekday-mornings">Weekday Mornings</option>
                        <option value="weekday-afternoons">Weekday Afternoons</option>
                        <option value="weekends">Weekends</option>
                        <option value="flexible">Flexible</option>
                        <option value="one-off">One-off Events Only</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="volunteer-interests">Which roles interest you? *</Label>
                      <Textarea id="volunteer-interests" name="interests" rows={4} required className="mt-2" />
                    </div>

                    <Button type="submit" size="lg" className="w-full bg-gradient-to-r from-[#2D5F3F] to-[#3A7D4A] hover:from-[#234A32] hover:to-[#2D5F3F] text-white font-semibold py-6 text-lg">
                      <Users className="w-5 h-5 mr-2" />
                      Submit Volunteer Application
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Trustee Tab */}
            <TabsContent value="trustee">
              <Card className="border-none shadow-xl">
                <CardContent className="p-8 md:p-12">
                  <div className="flex items-center mb-6">
                    <div className="bg-[#D4AF37] p-3 rounded-xl mr-4">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-[#2D5F3F]">Join as Trustee</h2>
                      <p className="text-gray-600">Strategic governance and leadership</p>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-[#2D5F3F] mb-4">What We're Looking For</h3>
                    <p className="text-gray-700 mb-4">
                      We seek committed individuals with expertise in finance, legal, healthcare, logistics, fundraising, or governance to provide strategic oversight and ensure accountability.
                    </p>
                    <ul className="space-y-3">
                      {[
                        "Commitment to our mission and values",
                        "Quarterly board meetings (in-person or virtual)",
                        "Strategic planning and policy development",
                        "Financial oversight and risk management",
                        "Ambassadorial role in networks"
                      ].map((requirement, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Check className="w-5 h-5 text-[#2D5F3F] mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{requirement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <form onSubmit={handleTrusteeSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="trustee-name">Full Name *</Label>
                        <Input id="trustee-name" required className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="trustee-email">Email Address *</Label>
                        <Input id="trustee-email" type="email" required className="mt-2" />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="trustee-phone">Phone Number *</Label>
                        <Input id="trustee-phone" type="tel" required className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="trustee-linkedin">LinkedIn Profile (optional)</Label>
                        <Input id="trustee-linkedin" type="url" className="mt-2" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="trustee-experience">Professional Background & Expertise *</Label>
                      <Textarea id="trustee-experience" rows={5} required className="mt-2" placeholder="Please outline your professional experience and areas of expertise relevant to charity governance" />
                    </div>

                    <div>
                      <Label htmlFor="trustee-motivation">Why do you want to join our board? *</Label>
                      <Textarea id="trustee-motivation" rows={4} required className="mt-2" />
                    </div>

                    <div>
                      <Label htmlFor="trustee-cv">CV/Resume *</Label>
                      <Input id="trustee-cv" type="file" accept=".pdf,.doc,.docx" required className="mt-2" />
                      <p className="text-xs text-gray-500 mt-1">PDF or Word document (max 5MB)</p>
                    </div>

                    <Button type="submit" size="lg" className="w-full bg-[#D4AF37] hover:bg-[#C19B2C] text-white font-semibold py-6 text-lg">
                      <Shield className="w-5 h-5 mr-2" />
                      Submit Trustee Application
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default GetInvolved;
